import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { 
  getCategories, 
  deleteCategory, 
  bulkUpdateCategories,
  exportCategories 
} from '../../services/api';
import { 
  FiSearch, 
  FiFilter, 
  FiRefreshCw, 
  FiEye,
  FiEdit2,
  FiTrash2,
  FiPlus,
  FiDownload,
  FiChevronDown,
  FiChevronRight,
  FiFolder,
  FiFolderPlus,
  FiGrid,
  FiLayers,
  FiPackage,
  FiCheckSquare,
  FiSquare,
  FiAlertCircle,
  FiCheckCircle,
  FiXCircle,
  FiArrowLeft,
  FiArrowRight,
  FiCornerDownRight,
  FiActivity
} from 'react-icons/fi';

function Categories({ admin }) {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [filters, setFilters] = useState({
    parent_id: '',
    search: '',
    include_subcategories: true
  });
  const [showFilters, setShowFilters] = useState(false);
  const [expandedCategories, setExpandedCategories] = useState([]);
  const [viewMode, setViewMode] = useState('hierarchical'); // hierarchical or flat
  const [showBulkActions, setShowBulkActions] = useState(false);

  const navigate = useNavigate();

  useEffect(() => {
    fetchCategories();
  }, [filters]);

  const fetchCategories = async () => {
    try {
      setLoading(true);
      setError('');
      const params = { ...filters };
      
      Object.keys(params).forEach(key => {
        if (params[key] === '') {
          delete params[key];
        }
      });

      const response = await getCategories(params);
      if (response.success) {
        setCategories(response.data || []);
      } else {
        setError(response.message || 'Failed to load categories');
      }
    } catch (err) {
      setError('Failed to load categories: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (categoryId, categoryName) => {
    if (!window.confirm(`Are you sure you want to delete "${categoryName}"? This action cannot be undone.`)) {
      return;
    }

    try {
      const response = await deleteCategory(categoryId);
      if (response.success) {
        setSuccessMessage(`Category "${categoryName}" deleted successfully!`);
        setTimeout(() => setSuccessMessage(''), 3000);
        fetchCategories();
        setSelectedCategories(prev => prev.filter(id => id !== categoryId));
      } else {
        throw new Error(response.message || 'Delete failed');
      }
    } catch (err) {
      setError('Failed to delete category: ' + err.message);
    }
  };

  const handleBulkAction = async (action) => {
    if (selectedCategories.length === 0) {
      setError('Please select categories first');
      setTimeout(() => setError(''), 3000);
      return;
    }

    try {
      if (action === 'delete') {
        if (!window.confirm(`Delete ${selectedCategories.length} selected categories? This action cannot be undone.`)) return;
        
        // Delete categories one by one
        let deletedCount = 0;
        for (const categoryId of selectedCategories) {
          const response = await deleteCategory(categoryId);
          if (response.success) deletedCount++;
        }
        
        setSuccessMessage(`Successfully deleted ${deletedCount} categories!`);
        setTimeout(() => setSuccessMessage(''), 3000);
        fetchCategories();
        setSelectedCategories([]);
        setShowBulkActions(false);
      }
    } catch (err) {
      setError('Failed to perform bulk action: ' + err.message);
    }
  };

  const handleExport = async (format) => {
    try {
      const response = await exportCategories(format);
      if (response.success) {
        // Create download link
        const blob = new Blob([format === 'json' ? JSON.stringify(response.data, null, 2) : response.data], {
          type: format === 'json' ? 'application/json' : 'text/csv'
        });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `categories_${new Date().toISOString().split('T')[0]}.${format}`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        
        setSuccessMessage(`Categories exported as ${format.toUpperCase()} successfully!`);
        setTimeout(() => setSuccessMessage(''), 3000);
      } else {
        throw new Error(response.message || 'Export failed');
      }
    } catch (err) {
      setError('Failed to export categories: ' + err.message);
    }
  };

  const toggleCategorySelection = (categoryId) => {
    setSelectedCategories(prev => 
      prev.includes(categoryId) 
        ? prev.filter(id => id !== categoryId)
        : [...prev, categoryId]
    );
    setShowBulkActions(true);
  };

  const toggleAllSelection = () => {
    if (selectedCategories.length === categories.length) {
      setSelectedCategories([]);
      setShowBulkActions(false);
    } else {
      const allCategoryIds = getAllCategoryIds(categories);
      setSelectedCategories(allCategoryIds);
      setShowBulkActions(true);
    }
  };

  const getAllCategoryIds = (catList) => {
    let ids = [];
    catList.forEach(cat => {
      ids.push(cat.category_id);
      if (cat.subcategories && cat.subcategories.length > 0) {
        ids = [...ids, ...getAllCategoryIds(cat.subcategories)];
      }
    });
    return ids;
  };

  const toggleExpandCategory = (categoryId) => {
    setExpandedCategories(prev => 
      prev.includes(categoryId) 
        ? prev.filter(id => id !== categoryId)
        : [...prev, categoryId]
    );
  };

  const getParentCategories = () => {
    return categories.filter(cat => !cat.parent_id);
  };

  const getTotalListingsCount = (category) => {
    let count = category.total_listings || 0;
    if (category.subcategories) {
      category.subcategories.forEach(sub => {
        count += getTotalListingsCount(sub);
      });
    }
    return count;
  };

  const getActiveListingsCount = (category) => {
    let count = category.active_listings || 0;
    if (category.subcategories) {
      category.subcategories.forEach(sub => {
        count += getActiveListingsCount(sub);
      });
    }
    return count;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const renderCategoryRow = (category, level = 0, isSubcategory = false) => {
    const isExpanded = expandedCategories.includes(category.category_id);
    const hasSubcategories = category.subcategories && category.subcategories.length > 0;
    
    return (
      <React.Fragment key={category.category_id}>
        {/* Main Category Row */}
        <tr className={`hover:bg-gray-750 transition-colors duration-150 ${level > 0 ? 'bg-gray-800/50' : ''}`}>
          <td className="px-6 py-4" style={{ paddingLeft: `${level * 32 + 24}px` }}>
            <div className="flex items-center">
              <input
                type="checkbox"
                checked={selectedCategories.includes(category.category_id)}
                onChange={() => toggleCategorySelection(category.category_id)}
                className="h-4 w-4 text-[#ff660e] focus:ring-[#ff660e] border-gray-600 rounded bg-gray-700"
              />
              
              {hasSubcategories && (
                <button
                  onClick={() => toggleExpandCategory(category.category_id)}
                  className="ml-2 text-gray-400 hover:text-white transition-colors"
                >
                  {isExpanded ? <FiChevronDown /> : <FiChevronRight />}
                </button>
              )}
              
              <div 
                className="w-10 h-10 rounded-lg flex items-center justify-center ml-3"
                style={{ backgroundColor: category.background_color || '#374151' }}
              >
                <span 
                  className="text-lg"
                  style={{ color: category.icon_color || '#9CA3AF' }}
                >
                  {category.icon_name || '📁'}
                </span>
              </div>
            </div>
          </td>
          
          <td className="px-6 py-4">
            <div className="flex items-center space-x-3">
              <div>
                <div className="text-sm font-semibold text-white flex items-center">
                  {level > 0 && (
                    <FiCornerDownRight className="text-gray-500 mr-2" />
                  )}
                  {category.name}
                </div>
                <div className="text-xs text-gray-500 mt-1">
                  <span className="bg-gray-700 px-2 py-0.5 rounded mr-2">{category.icon_library}</span>
                  <span>{category.icon_name}</span>
                </div>
              </div>
            </div>
          </td>
          
          <td className="px-6 py-4">
            <div className="text-sm">
              <div className="flex items-center space-x-2">
                <span className="font-medium text-white">{category.active_listings || 0}</span>
                <span className="text-gray-500">/</span>
                <span className="text-gray-400">{category.total_listings || 0}</span>
              </div>
              <div className="text-xs text-gray-500">
                Active / Total
              </div>
            </div>
          </td>
          
          <td className="px-6 py-4">
            <div className="text-sm text-gray-300">
              {category.subcategory_count || 0}
              {hasSubcategories && (
                <div className="text-xs text-gray-500">
                  {isExpanded ? 'Expanded' : 'Collapsed'}
                </div>
              )}
            </div>
          </td>
          
          <td className="px-6 py-4">
            <div className="text-sm text-gray-400">
              {formatDate(category.created_at)}
            </div>
          </td>
          
          <td className="px-6 py-4">
            <div className="flex space-x-3">
              <Link
                to={`/admin/categories/edit/${category.category_id}`}
                className="text-blue-400 hover:text-blue-300 transition-colors flex items-center"
                title="Edit Category"
              >
                <FiEdit2 className="mr-1" />
                <span className="text-sm">Edit</span>
              </Link>
              
              <button
                onClick={() => navigate(`/admin/categories/${category.category_id}`)}
                className="text-green-400 hover:text-green-300 transition-colors flex items-center"
                title="View Details"
              >
                <FiEye className="mr-1" />
                <span className="text-sm">View</span>
              </button>
              
              <button
                onClick={() => handleDelete(category.category_id, category.name)}
                className="text-red-400 hover:text-red-300 transition-colors flex items-center"
                title="Delete Category"
              >
                <FiTrash2 className="mr-1" />
                <span className="text-sm">Delete</span>
              </button>
            </div>
          </td>
        </tr>
        
        {/* Render subcategories if expanded */}
        {isExpanded && hasSubcategories && category.subcategories.map(sub => 
          renderCategoryRow(sub, level + 1, true)
        )}
      </React.Fragment>
    );
  };

  // Loading Skeleton
  if (loading && categories.length === 0) {
    return (
      <div className="min-h-screen bg-gray-900 p-6">
        <div className="animate-pulse">
          {/* Header Skeleton */}
          <div className="mb-6">
            <div className="h-8 bg-gray-700 rounded w-1/4 mb-2"></div>
            <div className="h-4 bg-gray-700 rounded w-1/2"></div>
          </div>

          {/* Table Skeleton */}
          <div className="bg-gray-800 rounded-xl border border-gray-700 overflow-hidden">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="p-6 border-b border-gray-700">
                <div className="flex justify-between items-center">
                  <div className="space-y-2">
                    <div className="h-4 bg-gray-700 rounded w-48"></div>
                    <div className="h-3 bg-gray-700 rounded w-32"></div>
                  </div>
                  <div className="h-6 bg-gray-700 rounded w-20"></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  const mainCategories = categories.filter(cat => !cat.parent_id);
  const totalListings = categories.reduce((sum, cat) => sum + (cat.total_listings || 0), 0);
  const activeListings = categories.reduce((sum, cat) => sum + (cat.active_listings || 0), 0);
  const totalSubcategories = categories.reduce((sum, cat) => sum + (cat.subcategory_count || 0), 0);

  return (
    <div className="min-h-full bg-gray-900 p-4 md:p-6">
      {/* Header */}
      <div className="mb-6">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-white">
              <span className="text-[#ff660e]">Categories</span> Management
            </h1>
            <p className="text-gray-400 mt-1">Manage product categories and subcategories hierarchy</p>
          </div>
          <div className="flex gap-3">
            <button
              onClick={() => handleExport('csv')}
              className="px-4 py-2.5 bg-gray-800 text-white border border-gray-700 rounded-lg hover:bg-gray-700 hover:border-green-500 transition-all duration-200 font-medium flex items-center hover:scale-[1.02]"
            >
              <FiDownload className="mr-2" />
              Export CSV
            </button>
            <button
              onClick={() => handleExport('json')}
              className="px-4 py-2.5 bg-gray-800 text-white border border-gray-700 rounded-lg hover:bg-gray-700 hover:border-blue-500 transition-all duration-200 font-medium flex items-center hover:scale-[1.02]"
            >
              <FiDownload className="mr-2" />
              Export JSON
            </button>
            <Link
              to="/admin/categories/new"
              className="px-4 py-2.5 bg-gradient-to-r from-[#ff660e] to-orange-500 text-white rounded-lg hover:from-[#ff5500] hover:to-orange-600 transition-all duration-200 font-medium flex items-center hover:scale-[1.02] shadow-lg"
            >
              <FiPlus className="mr-2" />
              New Category
            </Link>
          </div>
        </div>
      </div>

      {/* Success Message */}
      {successMessage && (
        <div className="mb-6 bg-green-900/20 border border-green-800/50 rounded-xl p-4 flex items-center justify-between animate-fade-in backdrop-blur-sm">
          <div className="flex items-center space-x-3">
            <FiCheckCircle className="text-green-400 text-xl" />
            <p className="text-green-300 font-medium">{successMessage}</p>
          </div>
          <button 
            onClick={() => setSuccessMessage('')}
            className="text-green-400 hover:text-green-300 text-xl"
          >
            ×
          </button>
        </div>
      )}

      {/* Error Alert */}
      {error && (
        <div className="mb-6 bg-red-900/20 border border-red-800/50 rounded-xl p-4 flex items-center justify-between animate-fade-in backdrop-blur-sm">
          <div className="flex items-center space-x-3">
            <FiAlertCircle className="text-red-400 text-xl" />
            <p className="text-red-300">{error}</p>
          </div>
          <button 
            onClick={() => setError('')}
            className="text-red-400 hover:text-red-300 text-xl"
          >
            ×
          </button>
        </div>
      )}

      {/* Quick Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-gray-800 rounded-xl border border-gray-700 p-5 hover:border-gray-600 transition-colors group">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-400 text-sm font-medium">Total Categories</p>
              <p className="text-2xl font-bold text-white mt-1 group-hover:text-[#ff660e] transition-colors">
                {categories.length}
              </p>
              <p className="text-sm text-gray-500 mt-1">
                {mainCategories.length} main, {totalSubcategories} sub
              </p>
            </div>
            <div className="w-12 h-12 rounded-lg bg-blue-900/30 flex items-center justify-center group-hover:scale-110 transition-transform">
              <FiFolder className="text-blue-400 text-2xl" />
            </div>
          </div>
        </div>
        
        <div className="bg-gray-800 rounded-xl border border-gray-700 p-5 hover:border-gray-600 transition-colors group">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-400 text-sm font-medium">Total Listings</p>
              <p className="text-2xl font-bold text-green-400 mt-1 group-hover:text-green-300 transition-colors">
                {totalListings}
              </p>
              <p className="text-sm text-gray-500 mt-1">
                {activeListings} active
              </p>
            </div>
            <div className="w-12 h-12 rounded-lg bg-green-900/30 flex items-center justify-center group-hover:scale-110 transition-transform">
              <FiPackage className="text-green-400 text-2xl" />
            </div>
          </div>
        </div>
        
        <div className="bg-gray-800 rounded-xl border border-gray-700 p-5 hover:border-gray-600 transition-colors group">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-400 text-sm font-medium">Selected</p>
              <p className="text-2xl font-bold text-[#ff660e] mt-1 group-hover:text-orange-400 transition-colors">
                {selectedCategories.length}
              </p>
              <p className="text-sm text-gray-500 mt-1">
                {selectedCategories.length > 0 ? 'Ready for action' : 'None selected'}
              </p>
            </div>
            <div className="w-12 h-12 rounded-lg bg-[#ff660e]/20 flex items-center justify-center group-hover:scale-110 transition-transform">
              <FiCheckSquare className="text-[#ff660e] text-2xl" />
            </div>
          </div>
        </div>
        
        <div className="bg-gray-800 rounded-xl border border-gray-700 p-5 hover:border-gray-600 transition-colors group">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-400 text-sm font-medium">Max Depth</p>
              <p className="text-2xl font-bold text-purple-400 mt-1 group-hover:text-purple-300 transition-colors">
                2
              </p>
              <p className="text-sm text-gray-500 mt-1">
                Parent → Subcategory
              </p>
            </div>
            <div className="w-12 h-12 rounded-lg bg-purple-900/30 flex items-center justify-center group-hover:scale-110 transition-transform">
              <FiLayers className="text-purple-400 text-2xl" />
            </div>
          </div>
        </div>
      </div>

      {/* Filters Card */}
      <div className="bg-gray-800 rounded-xl border border-gray-700 mb-6">
        <div className="p-4 border-b border-gray-700">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <FiFilter className="text-[#ff660e]" />
              <h3 className="font-medium text-white">Filters & Options</h3>
            </div>
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="text-sm text-[#ff660e] hover:text-orange-400 font-medium flex items-center transition-colors"
            >
              {showFilters ? 'Hide Filters' : 'Show Filters'}
            </button>
          </div>
        </div>

        {showFilters && (
          <div className="p-4 md:p-6">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              {/* Search */}
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-300 mb-2">Search Categories</label>
                <div className="relative">
                  <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500" />
                  <input
                    type="text"
                    placeholder="Search by name, icon, or library..."
                    value={filters.search}
                    onChange={(e) => setFilters({...filters, search: e.target.value})}
                    className="w-full pl-10 pr-3 py-2.5 bg-gray-700 border border-gray-600 rounded-lg focus:ring-2 focus:ring-[#ff660e] focus:border-transparent transition-all duration-200 text-white placeholder-gray-500"
                  />
                </div>
              </div>

              {/* Parent Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Parent Category</label>
                <select
                  value={filters.parent_id}
                  onChange={(e) => setFilters({...filters, parent_id: e.target.value})}
                  className="w-full px-3 py-2.5 bg-gray-700 border border-gray-600 rounded-lg focus:ring-2 focus:ring-[#ff660e] focus:border-transparent transition-all duration-200 text-white"
                >
                  <option value="">All Categories</option>
                  <option value="null">Main Categories Only</option>
                  {mainCategories.map(cat => (
                    <option key={cat.category_id} value={cat.category_id}>
                      {cat.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* View Mode */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">View Mode</label>
                <div className="flex gap-2">
                  <button
                    onClick={() => setViewMode('hierarchical')}
                    className={`flex-1 px-3 py-2 text-sm font-medium rounded-lg transition-all duration-200 ${
                      viewMode === 'hierarchical'
                        ? 'bg-[#ff660e] text-white'
                        : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                    }`}
                  >
                    <FiLayers className="inline mr-1" />
                    Hierarchical
                  </button>
                  <button
                    onClick={() => setViewMode('flat')}
                    className={`flex-1 px-3 py-2 text-sm font-medium rounded-lg transition-all duration-200 ${
                      viewMode === 'flat'
                        ? 'bg-[#ff660e] text-white'
                        : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                    }`}
                  >
                    <FiGrid className="inline mr-1" />
                    Flat
                  </button>
                </div>
              </div>
            </div>

            {/* Include Subcategories */}
            <div className="flex items-center mt-4">
              <input
                type="checkbox"
                checked={filters.include_subcategories}
                onChange={(e) => setFilters({...filters, include_subcategories: e.target.checked})}
                className="h-4 w-4 text-[#ff660e] focus:ring-[#ff660e] border-gray-600 rounded bg-gray-700"
              />
              <label className="ml-2 text-sm text-gray-300">
                Include Subcategories in search
              </label>
            </div>

            {/* Filter Actions */}
            <div className="flex justify-between items-center mt-6 pt-6 border-t border-gray-700">
              <button
                onClick={() => {
                  setFilters({
                    parent_id: '',
                    search: '',
                    include_subcategories: true
                  });
                  setExpandedCategories([]);
                  setSelectedCategories([]);
                  setShowBulkActions(false);
                }}
                className="px-4 py-2 text-gray-400 hover:text-gray-300 text-sm font-medium hover:bg-gray-700 rounded-lg transition-colors"
              >
                Clear All Filters
              </button>
              <div className="flex gap-3">
                <button
                  onClick={() => setShowFilters(false)}
                  className="px-4 py-2 text-gray-300 bg-gray-700 border border-gray-600 rounded-lg hover:bg-gray-600 transition-colors font-medium hover:scale-[1.02]"
                >
                  Cancel
                </button>
                <button
                  onClick={fetchCategories}
                  className="px-4 py-2 bg-gradient-to-r from-[#ff660e] to-orange-500 text-white rounded-lg hover:from-[#ff5500] hover:to-orange-600 transition-colors font-medium flex items-center hover:scale-[1.02]"
                >
                  <FiFilter className="mr-2" />
                  Apply Filters
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Bulk Actions Bar */}
      {showBulkActions && selectedCategories.length > 0 && (
        <div className="mb-6 bg-gray-800 rounded-xl border border-gray-700 p-4 animate-fade-in">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 rounded-lg bg-[#ff660e]/20 flex items-center justify-center">
                <FiCheckSquare className="text-[#ff660e]" />
              </div>
              <div>
                <h3 className="font-medium text-white">Bulk Actions</h3>
                <p className="text-sm text-gray-400">
                  {selectedCategories.length} categor{selectedCategories.length === 1 ? 'y' : 'ies'} selected
                </p>
              </div>
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => setSelectedCategories([])}
                className="px-4 py-2 text-gray-300 bg-gray-700 border border-gray-600 rounded-lg hover:bg-gray-600 hover:text-white transition-colors font-medium"
              >
                Clear Selection
              </button>
              <button
                onClick={() => handleBulkAction('delete')}
                className="px-4 py-2 bg-red-900/30 hover:bg-red-900/50 text-red-400 hover:text-red-300 rounded-lg font-medium flex items-center transition-all duration-200 hover:scale-[1.02] border border-red-800/50"
              >
                <FiTrash2 className="mr-2" />
                Delete Selected
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Categories Table */}
      <div className="bg-gray-800 rounded-xl border border-gray-700 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-700">
            <thead className="bg-gray-900">
              <tr>
                <th className="px-6 py-4 text-left text-xs font-medium text-gray-400 uppercase tracking-wider w-16">
                  <input
                    type="checkbox"
                    checked={categories.length > 0 && selectedCategories.length === getAllCategoryIds(categories).length}
                    onChange={toggleAllSelection}
                    className="h-4 w-4 text-[#ff660e] focus:ring-[#ff660e] border-gray-600 rounded bg-gray-700"
                  />
                </th>
                <th className="px-6 py-4 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                  Category Details
                </th>
                <th className="px-6 py-4 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                  Listings
                </th>
                <th className="px-6 py-4 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                  Subcategories
                </th>
                <th className="px-6 py-4 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                  Created
                </th>
                <th className="px-6 py-4 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-gray-800 divide-y divide-gray-700">
              {mainCategories.length > 0 ? (
                viewMode === 'hierarchical' ? (
                  mainCategories.map(category => renderCategoryRow(category, 0, false))
                ) : (
                  categories.map(category => renderCategoryRow(category, category.parent_id ? 1 : 0, !!category.parent_id))
                )
              ) : (
                <tr>
                  <td colSpan="6" className="px-6 py-12 text-center">
                    <div className="text-gray-600 text-6xl mb-6">📁</div>
                    <h3 className="text-xl font-medium text-white mb-2">No categories found</h3>
                    <p className="text-gray-400 mb-6">Try adjusting your search or create the first category</p>
                    <Link
                      to="/admin/categories/new"
                      className="px-6 py-2.5 bg-gradient-to-r from-[#ff660e] to-orange-500 text-white rounded-lg hover:from-[#ff5500] hover:to-orange-600 transition-colors font-medium hover:scale-[1.02] inline-flex items-center"
                    >
                      <FiPlus className="mr-2" />
                      Create First Category
                    </Link>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* CSS for animations */}
      <style jsx>{`
        @keyframes fade-in {
          from { opacity: 0; transform: translateY(-10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fade-in {
          animation: fade-in 0.3s ease-out;
        }
      `}</style>
    </div>
  );
}

export default Categories;